/**
 * Application entry.
 *
 * Wires the api layer's auth hooks to the session source and the token manager,
 * resumes a persisted session, exposes the developer console seam, requests
 * persistent storage, registers the auto-updating service worker, and mounts the
 * router. No view logic lives here.
 */

import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { registerSW } from 'virtual:pwa-register';

import App from './App';
import { LanguageProvider, initialLocaleState, resolveDocumentLocale } from './i18n';
import { registerFonts } from './lib/fonts';
import './styles/main.css';

import { config } from './config';
import {
  setAuthTokenProvider,
  setAuthRequestScopeProvider,
  setTokenRefresher,
  set401Handler,
  setSwapIdentityProvider,
} from './lib/api';
import {
  getToken,
  setToken,
  clearToken,
  hasToken,
  isRealSession,
} from './lib/session';
import {
  ensureFreshAccessToken,
  refreshAfter401,
  setActiveAccessTokenGetter,
  startAccessTokenRefreshScheduler,
  startSessionWatchdog,
  logout,
  resumePersistedAuthSession,
  getAuthRequestScope,
} from './lib/auth';
import { getSwapIdentityToken } from './lib/swapToken';

// The api layer reads the bearer token and drives refresh through these hooks so
// it stays decoupled from the session and OAuth modules.
setAuthTokenProvider(getToken);
setAuthRequestScopeProvider(getAuthRequestScope);
setTokenRefresher((recovery, signal) =>
  ensureFreshAccessToken(60, recovery, signal),
);
set401Handler(refreshAfter401);
setActiveAccessTokenGetter(getToken);

// Swap-service calls mint and cache a per-user identity token through this hook.
// The api layer attaches it as X-Swap-Identity only for requests that opt in, so
// ordinary gateway calls never touch it.
setSwapIdentityProvider(getSwapIdentityToken);

// Resume a real session persisted across a reload. The access token is not
// stored, so a resumed session has only its refresh token until the first authed
// read triggers a proactive refresh — which is why session presence, not
// hasToken(), is what says "authenticated" this early (see the lang block below).
await resumePersistedAuthSession();

// A tiny documented console seam. Real OAuth sign-in is the primary path; this
// stays as a developer fallback and carries no secret beyond the token supplied.
(globalThis as unknown as { plantmewallet: unknown }).plantmewallet = Object.freeze({
  setToken,
  clearToken,
  hasToken,
  logout,
  get gatewayBaseUrl() {
    return config.gatewayBaseUrl;
  },
});

// Ask the browser to keep storage persistent so the OS does not evict app data.
if (config.flags.requirePersistentStorage && navigator.storage?.persist) {
  navigator.storage.persist().catch(() => {
    // Best effort; the app still works without a persistence grant.
  });
}

// Watch for session expiry from here on. Started after the resume above so the
// first check sees the resumed session rather than an empty one, and before the
// render so an expired social session is torn down immediately. An expired
// passkey session instead enters the recoverable state before routing begins, so
// an already-open transfer can request one passkey ceremony without losing its
// local form state.
await startSessionWatchdog();
startAccessTokenRefreshScheduler();

// Auto-updating worker: a new deploy takes over on the next load. Content-hashed
// assets make that safe, and the gateway API is forced NetworkOnly in the config.
registerSW({ immediate: true });

// Set <html lang> BEFORE first render, so the very first paint already uses the
// correct --font-sans (Pretendard, or Pretendard JP under html[lang='ja']) — a cold
// Japanese session never requests the base font first, and an English screen never
// pulls the ~5 MB JP webfont.
//
// Same resolver the app uses at runtime (useDocumentLanguage in App), so the
// pre-render value and the first post-mount value agree; App owns it from then on.
//
// `authed` must match what useAuthed() will report, which is NOT hasToken(): a
// resumed session is authenticated while its access token is still null, so
// hasToken() would read false and the two would disagree on the first paint.
//
// Read live through isRealSession() rather than from the resume call's return
// value: the watchdog above runs in between and may have just torn down a session
// that expired while the app was closed. The stale flag would then claim authed
// for a user who is about to land on the sign-in screen.
const { locale: initialLocale, chosen: localeSettled } = initialLocaleState();
document.documentElement.lang = resolveDocumentLocale({
  locale: initialLocale,
  localeChosen: localeSettled,
  pathname: window.location.pathname,
  authed: isRealSession() || hasToken(),
});

// Register the self-hosted Pretendard faces (loaded on demand — JP only when a
// Japanese page needs it). Declared here rather than an index.html @font-face so
// the URL carries SHARED_ORIGIN and font loading avoids the style-src CSP.
registerFonts();

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('mount point #root not found');

createRoot(rootEl).render(
  <LanguageProvider>
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </LanguageProvider>,
);
